import { useEffect, useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import posthog from "posthog-js";
import { useTranslation } from "react-i18next";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";

const DISMISS_KEY = "pastDueBannerDismissedAt";

/** Session-scoped dismissal: the banner comes back on the next visit as long
 * as the subscription is still past_due — a billing failure should keep
 * nagging until it's fixed, just not within the same browsing session. */
function isDismissedThisSession() {
  try {
    return Boolean(sessionStorage.getItem(DISMISS_KEY));
  } catch {
    return false;
  }
}

function planDisplayName(plan: string) {
  const base = plan.split("_")[0] ?? plan;
  return base.charAt(0).toUpperCase() + base.slice(1);
}

/**
 * In-app half of the failed-payment (dunning) recovery loop: when a Stripe
 * renewal charge fails the subscription goes past_due and Stripe retries for
 * a few weeks. The dunning email alone recovers ~0% — this banner points the
 * user at Stripe's hosted invoice page, where they can update their card and
 * pay the open invoice in one step. Web-only: IAP billing is Apple/Google's.
 */
export default function PastDuePaymentBanner() {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [hidden, setHidden] = useState(() => isDismissedThisSession());
  const shownTrackedRef = useRef(false);

  const { data: subscription } = trpc.payment.getSubscription.useQuery(
    undefined,
    { enabled: isAuthenticated && !hidden },
  );
  const isPastDueStripe =
    !!subscription &&
    subscription.status === "past_due" &&
    !!subscription.stripeSubscriptionId;

  const { data: issue } = trpc.payment.getBillingIssue.useQuery(undefined, {
    enabled: isPastDueStripe && !hidden,
    staleTime: 5 * 60 * 1000,
  });

  const visible = !hidden && isPastDueStripe && !!issue;

  useEffect(() => {
    if (visible && !shownTrackedRef.current) {
      shownTrackedRef.current = true;
      posthog.capture("past_due_banner_shown", {
        plan: issue?.plan,
        amount_due: (issue?.amountDue ?? 0) / 100,
        attempt_count: issue?.attemptCount,
      });
    }
  }, [visible, issue]);

  if (!visible || !issue) return null;

  const amountFormatted = `${issue.currency.toUpperCase()} ${(
    issue.amountDue / 100
  ).toFixed(2)}`;

  const handleFixPayment = () => {
    posthog.capture("past_due_banner_clicked", {
      plan: issue.plan,
      amount_due: issue.amountDue / 100,
    });
    window.open(issue.hostedInvoiceUrl, "_blank", "noopener");
  };

  const handleDismiss = () => {
    posthog.capture("past_due_banner_dismissed", { plan: issue.plan });
    try {
      sessionStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // Storage unavailable (private mode) — hide for this render only.
    }
    setHidden(true);
  };

  return (
    <div className="fixed bottom-20 left-1/2 z-50 w-[calc(100%-2rem)] max-w-[600px] -translate-x-1/2 lg:bottom-6">
      <div
        className="relative overflow-hidden rounded-[14px]"
        style={{
          background:
            "linear-gradient(150deg, #1c1512 0%, #150d0c 55%, #1e1210 100%)",
          border: "1px solid rgba(251,146,60,0.25)",
          boxShadow:
            "0 24px 60px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)",
        }}
      >
        {/* Ambient amber glow */}
        <div
          className="pointer-events-none absolute"
          style={{
            top: "-60px",
            right: "120px",
            width: "260px",
            height: "180px",
            background:
              "radial-gradient(circle, rgba(251,146,60,0.25), transparent 68%)",
            filter: "blur(20px)",
          }}
        />

        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="absolute right-3 top-3 z-[2] flex h-[30px] w-[30px] items-center justify-center rounded-full text-[#5b6272] transition-colors hover:bg-white/[0.08] hover:text-[#d1d5e0]"
        >
          <X className="h-4 w-4" strokeWidth={2.2} />
        </button>

        <div className="relative flex items-center gap-[14px] py-[14px] pl-4 pr-11">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(251,146,60,0.12)]">
            <AlertTriangle className="h-[18px] w-[18px] text-[#fb923c]" strokeWidth={2.2} />
          </div>

          <div className="min-w-0 flex-1">
            <h2
              className="m-0 mb-1 text-[15px] font-bold text-white"
              style={{ letterSpacing: "-0.01em" }}
            >
              {t("subscription.pastDueBanner.title")}
            </h2>
            <p
              className="m-0 text-[12px] text-[#9ca3b4]"
              style={{ lineHeight: 1.4 }}
            >
              {issue.plan
                ? t("subscription.pastDueBanner.body", {
                    plan: planDisplayName(issue.plan),
                    amount: amountFormatted,
                  })
                : t("subscription.pastDueBanner.bodyNoPlan")}
            </p>
          </div>

          <div className="flex shrink-0 items-center pr-1.5">
            <button
              onClick={handleFixPayment}
              className="relative cursor-pointer whitespace-nowrap rounded-full px-5 py-2.5 text-[13px] font-bold text-white transition-transform hover:-translate-y-px active:translate-y-0"
              style={{
                background: "linear-gradient(90deg, #f97316, #fb923c)",
                boxShadow:
                  "0 12px 30px -8px rgba(249,115,22,0.6), inset 0 1px 0 rgba(255,255,255,0.3)",
                letterSpacing: "-0.01em",
              }}
            >
              {t("subscription.pastDueBanner.cta")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
